import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

/**
 * Draft state for an editor, mirrored to localStorage so typed-but-unsaved
 * text survives anything: tab crash, reload, accidental navigation, or the
 * app remounting. One mirror per file path (`dw-edit:<path>`), shared by every
 * editor surface (inline page editor, markdown source overlay).
 *
 * Semantics:
 * - Mount (or switching files): if a mirror exists and differs from the
 *   server content, restore it (`restored` is true so the UI can say so).
 * - Server content changes under a CLEAN draft (someone published, a sync
 *   landed, our own save round-tripped): follow the server.
 * - Server content changes under a DIRTY draft: keep the user's text — never
 *   clobber in-progress work. Their save then wins, visibly.
 * - The mirror is dropped when the draft matches the server again, after a
 *   successful save (`clearMirror`), or via `discardRestored`.
 *
 * Mirror writes are debounced 300ms, and flushed synchronously on pagehide
 * (reload/close) and unmount (in-app navigation) so the mirror never trails
 * the textarea. `clearMirror` disarms those flushes so a save's teardown
 * can't resurrect the mirror it just removed.
 */

function draftKey(path: string): string {
  return `dw-edit:${path}`;
}

export function readLocalDraft(path: string): string | null {
  try {
    return localStorage.getItem(draftKey(path));
  } catch {
    return null;
  }
}

export function writeLocalDraft(path: string, content: string | null): void {
  try {
    if (content === null) localStorage.removeItem(draftKey(path));
    else localStorage.setItem(draftKey(path), content);
  } catch {
    /* storage full/blocked — editing still works, just without the net */
  }
}

export function useLocalDraft(
  path: string,
  serverContent: string,
): {
  draft: string;
  setDraft: Dispatch<SetStateAction<string>>;
  /** True when the draft was recovered from the local mirror on mount. */
  restored: boolean;
  /** Throw away the recovered draft and return to the server content. */
  discardRestored: () => void;
  /** Call after a successful save: the worktree has the text, mirror done. */
  clearMirror: () => void;
} {
  // Mount: prefer the local mirror over the server content (crash recovery).
  const [draft, setDraft] = useState(() => {
    const saved = readLocalDraft(path);
    return saved !== null && saved !== serverContent ? saved : serverContent;
  });
  const [restored, setRestored] = useState(
    () => readLocalDraft(path) !== null && readLocalDraft(path) !== serverContent,
  );

  // Re-seed on prop change, synchronously during render (the React-documented
  // "adjust state when props change" pattern) so a restored draft never
  // flashes the server content first.
  const [prev, setPrev] = useState({ path, server: serverContent });
  if (prev.path !== path) {
    // Switched files: same seeding as mount.
    setPrev({ path, server: serverContent });
    const saved = readLocalDraft(path);
    setDraft(saved !== null && saved !== serverContent ? saved : serverContent);
    setRestored(saved !== null && saved !== serverContent);
  } else if (prev.server !== serverContent) {
    // Same file, server moved (publish, background sync, save round-trip):
    // follow only when the draft was clean — never clobber typed text.
    setPrev({ path, server: serverContent });
    if (draft === prev.server) setDraft(serverContent);
  }

  // Mirror the draft (debounced); drop the mirror when it matches the server.
  const flushed = useRef(false);
  const flushRef = useRef<() => void>(() => {});
  useEffect(() => {
    flushed.current = false;
    flushRef.current = () => {
      if (flushed.current) return;
      writeLocalDraft(path, draft === serverContent ? null : draft);
    };
    const id = setTimeout(flushRef.current, 300);
    return () => clearTimeout(id);
  }, [draft, serverContent, path]);

  // Flush on reload/close and on unmount, so the debounce window can't lose
  // the tail of what was typed.
  useEffect(() => {
    const flush = (): void => flushRef.current();
    window.addEventListener("pagehide", flush);
    return () => {
      flush();
      window.removeEventListener("pagehide", flush);
    };
  }, []);

  const clearMirror = (): void => {
    writeLocalDraft(path, null);
    flushed.current = true;
    setRestored(false);
  };

  const discardRestored = (): void => {
    writeLocalDraft(path, null);
    setDraft(serverContent);
    setRestored(false);
  };

  return { draft, setDraft, restored, discardRestored, clearMirror };
}

/** The "we recovered your unsaved edits" banner, shared by the editors. */
export function RestoredNotice({
  restored,
  onDiscard,
}: {
  restored: boolean;
  onDiscard: () => void;
}) {
  if (!restored) return null;
  return (
    <div className="dw-edit-restored">
      Restored unsaved edits from this browser.
      <button type="button" onClick={onDiscard}>
        discard them
      </button>
    </div>
  );
}
